/**
 * Phase 15.6M — Now Online activity strip.
 *
 * Horizontal scroller showing currently Live + Online + Away/BRB artists
 * so RGM Studio feels alive the moment someone opens the home page.
 * Each chip links to the artist profile (or /live for Live status).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import PresenceDot from "@/components/PresenceDot";
import { optimizeImage } from "@/lib/mediaUrls";

const STATUS_BADGE = {
  live: { label: "LIVE", className: "bg-red-500 text-white animate-pulse" },
  online: { label: "Online", className: "bg-emerald-500/90 text-black" },
  away: { label: "Away", className: "bg-amber-500/90 text-black" },
  brb: { label: "BRB", className: "bg-purple-500/90 text-white" },
};

const NowOnlineStrip = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const fetchActive = async () => {
      try {
        const r = await api.get("/presence/active", { params: { limit: 14 } });
        if (!cancelled) {
          setData(r.data);
          setLoading(false);
        }
      } catch (_e) {
        if (!cancelled) setLoading(false);
      }
    };
    fetchActive();
    const id = setInterval(fetchActive, 60000); // refresh every 60s
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  if (loading) return null;
  const items = data?.active || [];
  if (items.length === 0) return null;

  const counts = data?.counts || {};

  return (
    <section
      className="mb-6"
      data-testid="now-online-strip"
      aria-label="Artists currently active on RGM Studio"
    >
      <div className="flex items-baseline justify-between mb-2 px-1">
        <h2 className="text-white font-bold text-base flex items-center gap-2">
          <span className="inline-block w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
          Now on RGM
        </h2>
        <div className="text-[11px] text-emerald-200/70 tabular-nums" data-testid="now-online-counts">
          {counts.live > 0 && <span className="text-red-300">🔴 {counts.live} live</span>}
          {counts.live > 0 && (counts.online + counts.away_brb) > 0 && <span> · </span>}
          {(counts.online + counts.away_brb) > 0 && <span>{counts.online + counts.away_brb} online</span>}
        </div>
      </div>
      <div
        className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1 snap-x"
        style={{ scrollbarWidth: "thin" }}
        data-testid="now-online-scroller"
      >
        {items.map((a) => {
          const status = a.presence_status || "online";
          const badge = STATUS_BADGE[status] || STATUS_BADGE.online;
          const href = status === "live" ? "/live" : `/artist/${a.user_id}`;
          return (
            <Link
              key={a.user_id}
              to={href}
              className="group flex-shrink-0 w-20 sm:w-24 snap-start text-center"
              data-testid={`now-online-chip-${a.user_id}`}
              title={a.presence_status_note ? `${badge.label} — ${a.presence_status_note}` : `${badge.label} · Tap to view`}
            >
              <div className={`relative w-16 h-16 sm:w-20 sm:h-20 mx-auto rounded-full overflow-hidden border-2 ${status === "live" ? "border-red-500 shadow-[0_0_18px_rgba(239,68,68,0.5)]" : "border-emerald-400/40"} group-hover:scale-105 transition-transform`}>
                {a.profile_photo ? (
                  <img
                    src={optimizeImage(a.profile_photo, 'avatar')}
                    alt={a.artist_name}
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.style.display = "none"; }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-700 to-pink-700 text-white font-bold text-xl">
                    {(a.artist_name || "?").charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="absolute -bottom-0.5 right-0">
                  <PresenceDot status={status} size="md" />
                </div>
              </div>
              <p className="mt-1.5 text-[11px] sm:text-xs text-white truncate group-hover:text-emerald-300 transition-colors">
                {a.artist_name}
              </p>
              <span className={`inline-block mt-0.5 px-1.5 py-0 rounded text-[8px] font-bold uppercase tracking-wider ${badge.className}`}>
                {badge.label}
              </span>
              {/* iter_194b — Show custom Away/BRB note under the badge
                  so context is visible without hovering. Live/Online
                  hide the note (nothing meaningful to add). */}
              {(status === "away" || status === "brb") && a.presence_status_note && (
                <p
                  className="mt-0.5 text-[9px] text-gray-300 truncate leading-tight"
                  data-testid={`now-online-note-${a.user_id}`}
                >
                  {a.presence_status_note}
                </p>
              )}
            </Link>
          );
        })}
      </div>
    </section>
  );
};

export default NowOnlineStrip;
